import React, { useState } from 'react';
import { Button, Form, Input, message, Modal, Popconfirm, Select, Space, Table, Tag, Typography, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import type { Document } from '../../types';
import {
  useDeleteDocumentMutation,
  useGetDocumentsByEntityQuery,
  useUploadDocumentMutation,
} from '../../store/api/documentsApi';
import { downloadDocument, resolveDocumentFilename, viewDocument } from '../../utils/documentDownload';

const { Text } = Typography;

type EntityType = 'driver' | 'vehicle';

type DocumentTypeOption = {
  value: string;
  label: string;
};

const DRIVER_DOCUMENT_TYPES: DocumentTypeOption[] = [
  { value: 'PASSPORT', label: 'Паспорт' },
  { value: 'DRIVER_LICENSE', label: 'Водительское удостоверение' },
];

const VEHICLE_DOCUMENT_TYPES: DocumentTypeOption[] = [
  { value: 'VEHICLE_REGISTRATION', label: 'СТС' },
];

function documentTypeLabel(entityType: EntityType, value: string): string {
  const options = entityType === 'driver' ? DRIVER_DOCUMENT_TYPES : VEHICLE_DOCUMENT_TYPES;
  return options.find((option) => option.value === value)?.label || value || '—';
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
}

export interface EntityDocumentsBlockProps {
  entityType: EntityType;
  entityId: number;
  canModify?: boolean;
  documentDetails?: Partial<Record<string, {
    number?: string | null;
    issueDate?: string | null;
    expiresAt?: string | null;
  }>>;
}

function documentStatus(expiresAt?: string | null): { label: string; color: string } | null {
  if (!expiresAt) return null;
  const date = dayjs(expiresAt).startOf('day');
  if (!date.isValid()) return null;
  const today = dayjs().startOf('day');
  if (date.isBefore(today)) return { label: 'Просрочен', color: 'red' };
  if (date.diff(today, 'day') <= 30) return { label: 'Истекает', color: 'orange' };
  return { label: 'Актуален', color: 'green' };
}

function documentTypeOrder(entityType: EntityType, value: string): number {
  const options = entityType === 'driver' ? DRIVER_DOCUMENT_TYPES : VEHICLE_DOCUMENT_TYPES;
  const index = options.findIndex((option) => option.value === value);
  return index === -1 ? options.length : index;
}

const EntityDocumentsBlock: React.FC<EntityDocumentsBlockProps> = ({ entityType, entityId, canModify = false, documentDetails = {} }) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { data = [], isLoading, refetch } = useGetDocumentsByEntityQuery({ entityType, entityId });
  const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();

  const typeOptions = entityType === 'driver' ? DRIVER_DOCUMENT_TYPES : VEHICLE_DOCUMENT_TYPES;
  const existingTypes = new Set(data.map((doc) => String(doc.documentType || '').toUpperCase()));
  const availableTypeOptions = typeOptions.filter((option) => !existingTypes.has(option.value));
  const sortedDocuments = [...data].sort((a, b) => {
    const typeDiff = documentTypeOrder(entityType, a.documentType) - documentTypeOrder(entityType, b.documentType);
    if (typeDiff !== 0) return typeDiff;
    return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
  });

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (existingTypes.has(String(values.documentType || '').toUpperCase())) {
      message.warning('Документ этого типа уже загружен');
      return;
    }
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.error('Выберите файл');
      return;
    }
    await uploadDocument({
      entityType,
      entityId,
      documentType: values.documentType,
      comment: values.comment?.trim() || undefined,
      fileType: 'file',
      file,
      originalName: file.name,
    }).unwrap();
    message.success('Документ добавлен');
    setOpen(false);
    setFileList([]);
    form.resetFields();
    void refetch();
  };

  const columns: ColumnsType<Document> = [
    {
      title: 'Тип документа',
      dataIndex: 'documentType',
      render: (value: string) => documentTypeLabel(entityType, value),
    },
    {
      title: 'Номер',
      width: 140,
      render: (_: unknown, record) => documentDetails[record.documentType]?.number?.trim() || '—',
    },
    {
      title: 'Дата выдачи',
      width: 130,
      render: (_: unknown, record) => formatDate(documentDetails[record.documentType]?.issueDate),
    },
    {
      title: 'Срок действия',
      width: 130,
      render: (_: unknown, record) => formatDate(documentDetails[record.documentType]?.expiresAt),
    },
    {
      title: 'Статус',
      width: 120,
      render: (_: unknown, record) => {
        const status = documentStatus(documentDetails[record.documentType]?.expiresAt);
        return status ? <Tag color={status.color}>{status.label}</Tag> : '—';
      },
    },
    {
      title: 'Файл',
      dataIndex: 'filePath',
      ellipsis: true,
      render: (_: unknown, record) => resolveDocumentFilename(record),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 260,
      render: (_: unknown, record) => (
        <Space>
          <Button size="small" onClick={() => void viewDocument(record.id).catch(() => message.error('Не удалось открыть файл'))}>
            Просмотр
          </Button>
          <Button size="small" onClick={() => void downloadDocument(record.id, record).catch(() => message.error('Не удалось скачать файл'))}>
            Скачать
          </Button>
          {canModify ? (
            <Popconfirm
              title="Удалить документ?"
              onConfirm={async () => {
                await deleteDocument(record.id).unwrap();
                message.success('Документ удалён');
                void refetch();
              }}
            >
              <Button size="small" danger loading={isDeleting}>
                Удалить
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Text type="secondary">Документы хранятся в общей системе документов.</Text>
        {canModify ? (
          <Button
            type="primary"
            disabled={availableTypeOptions.length === 0}
            onClick={() => setOpen(true)}
          >
            Добавить документ
          </Button>
        ) : null}
      </Space>

      <Table<Document>
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={sortedDocuments}
        pagination={{ pageSize: 10, showTotal: (total) => `Показано: ${total}` }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title="Добавить документ"
        open={open}
        onCancel={() => {
          setOpen(false);
          setFileList([]);
          form.resetFields();
        }}
        onOk={() => void handleSubmit().catch((error) => {
          message.error(error?.data?.error || error?.message || 'Не удалось добавить документ');
        })}
        okButtonProps={{ loading: isUploading }}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="documentType" label="Тип документа" rules={[{ required: true, message: 'Выберите тип документа' }]}>
            <Select options={availableTypeOptions} placeholder="Выберите тип" />
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>
          <Form.Item label="Файл" required>
            <Upload
              beforeUpload={() => false}
              maxCount={1}
              fileList={fileList}
              onChange={({ fileList: next }) => setFileList(next)}
            >
              <Button icon={<UploadOutlined />}>Выбрать файл</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default EntityDocumentsBlock;
